import React, { useState, useEffect, useMemo, useContext } from "react";
import { fetchCompaniesAPI } from "../../services/delivery_api";
import CommonTitle from "../common/DeliveryCommonTitle";
import { signUpApi } from "../../services/delivery_api";
import { useDeliverySession } from "../../hooks/useDeliverySession";
import { useTranslation } from "react-i18next"; // ← ADD THIS
import { AuthContext } from "../../context/AuthContext";

// ✅ Static Most Popular Brands (updated to match image exactly)
const DEFAULT_POPULAR_NAMES = [
  "Amazon",
  "Flipkart",
  "Myntra",
  "Ajio",
  "Nykaa",
  "Meesho",
  "ZARA",
  "Tata Cliq",
  "Snapdeal",
  "H&M",
  "Pantaloons",
  "Reliance Trends",
  "Shoppers Stop",
  "Lifestyle",
  "Westside",
  "Fabindia",
  "Max Fashion",
  "Marks & Spencer",
  "Purplle",
  "Mamaearth",
  "Sugar Cosmetics",
  "Bata",
  "Woodland",
  "Puma",
  "Nike",
  "Adidas",
  "Reebok",
  "Titan",
  "Louis Philippe",
  "Allen Solly",
  "Van Heusen",
  "Bluedart",
];

// Custom hook for user-specific past companies
const useUserPastCompanies = (userId) => {
  const [pastCompanyIds, setPastCompanyIds] = useState([]);

  // Get user-specific storage key
  const getStorageKey = () => `pastCompanyIds_${userId}`;

  // Load past companies for this user
  useEffect(() => {
    if (!userId) {
      setPastCompanyIds([]);
      return;
    }

    const storageKey = getStorageKey();
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setPastCompanyIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("❌ Error parsing pastCompanyIds:", error);
        setPastCompanyIds([]);
      }
    } else {
      setPastCompanyIds([]);
    }
  }, [userId]); // Re-run when userId changes

  // Add a company to user's past companies
  const addCompanyId = (companyId) => {
    if (!userId) return;

    setPastCompanyIds((prev) => {
      // Remove if already exists to avoid duplicates
      const filtered = prev.filter((id) => id !== companyId);
      // Add to beginning (most recent first)
      const updated = [companyId, ...filtered];

      // Save to user-specific localStorage
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(updated));

      return updated;
    });
  };

  // Clear user's past companies (optional)
  const clearPastCompanies = () => {
    if (!userId) return;

    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    setPastCompanyIds([]);
  };

  return {
    pastCompanyIds,
    addCompanyId,
    clearPastCompanies,
    setPastCompanyIds,
  };
};

const SearchCompany = ({ onNext, updateDeliveryField }) => {
  const { updateUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFrequency, setSearchFrequency] = useState({});
  const { session } = useDeliverySession();

  const { pastCompanyIds, addCompanyId } = useUserPastCompanies(session?.da_id);

  // Load past companies from localStorage - FIXED: Check both session and localStorage
  useEffect(() => {
    // First check if there's a session company
    if (session?.company_id) {
      return;
    }

    // Then check localStorage
    // const stored = localStorage.getItem("pastCompanyIds");
    // if (stored) {
    //   try {
    //     const parsed = JSON.parse(stored);
    //     setPastCompanyIds(Array.isArray(parsed) ? parsed : []);
    //   } catch (error) {
    //     console.error("❌ Error parsing pastCompanyIds:", error);
    //     setPastCompanyIds([]);
    //   }
    // }
  }, [session]);

  // Handle company selection - FIXED: Properly update state
  const handleSelect = async (company) => {
    try {
      updateDeliveryField("company_nm", company.company_name);
      updateDeliveryField("company_img", company.company_img || "");

      const payload = { da_name: session.da_name, company_id: company.id };
      const response = await signUpApi(payload);

      const deliveryData = response?.data?.delivery_data;

      if (deliveryData) {
        updateUser({
          company: deliveryData.company,
          profile_completed: deliveryData.profile_completed,
          da_name: deliveryData.da_name,
          
        });
      }
      // Update past companies - most recent first
      let updatedPastCompanyIds;
      // setPastCompanyIds((prev) => {
      //   // Remove if already exists to avoid duplicates
      //   const filtered = prev.filter((id) => id !== company.id);
      //   // Add to beginning (most recent)
      //   updatedPastCompanyIds = [company.id, ...filtered];
      //   return updatedPastCompanyIds;
      // });

      // Save to localStorage immediately
      // localStorage.setItem(
      //   "pastCompanyIds",
      //   JSON.stringify([
      //     company.id,
      //     ...pastCompanyIds.filter((id) => id !== company.id),
      //   ])
      // );

      // Add company to this user's past companies
      addCompanyId(company.id);

      onNext();
    } catch (error) {
      console.error("❌ Error updating profile:", error);
    }
  };

  // Save past companies to localStorage when state changes
  useEffect(() => {
    if (pastCompanyIds.length > 0) {
      localStorage.setItem("pastCompanyIds", JSON.stringify(pastCompanyIds));
    }
  }, [pastCompanyIds]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetchCompaniesAPI();
        setCompanies(response);
      } catch (error) {
        console.error("❌ Failed to fetch companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  // Search frequency tracking
  useEffect(() => {
    if (searchTerm.trim()) {
      const matched = companies.find((c) =>
        c.company_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      if (matched) {
        setSearchFrequency((prev) => ({
          ...prev,
          [matched.id]: (prev[matched.id] || 0) + 1,
        }));
      }
    }
  }, [searchTerm, companies]);

  // Filtered search results
  const filtered = useMemo(() => {
    if (!searchTerm) return [];
    return companies.filter((c) =>
      c.company_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [companies, searchTerm]);

  // Alphabetical grouping
  const groupedAlphabetically = useMemo(() => {
    const grouped = {};
    companies.forEach((c) => {
      const letter = c.company_name[0].toUpperCase();
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(c);
    });
    return grouped;
  }, [companies]);

  // Most popular
  const mostPopular = useMemo(() => {
    return companies.filter((c) =>
      DEFAULT_POPULAR_NAMES.includes(c.company_name),
    );
  }, [companies]);

  // Past companies for current user (most recent first)
  const pastCompanies = useMemo(() => {
    return companies
      .filter((c) => pastCompanyIds.includes(c.id))
      .sort((a, b) => {
        // Sort by index in pastCompanyIds array (most recent first)
        return pastCompanyIds.indexOf(a.id) - pastCompanyIds.indexOf(b.id);
      });
  }, [companies, pastCompanyIds]);

  // Most searched
  const mostSearched = useMemo(() => {
    return Object.entries(searchFrequency)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => companies.find((c) => c.id === parseInt(id)))
      .filter(Boolean)
      .slice(0, 6);
  }, [searchFrequency, companies]);

  // Brand Card
  const BrandCard = ({ brand }) => (
    <div
      onClick={() => handleSelect(brand)}
      className="min-w-[80px] h-[110px] flex flex-col items-center justify-center text-center cursor-pointer hover:shadow-md transition-all duration-200 flex-shrink-0 p-3"
    >
      {brand.company_img ? (
        <img
          src={brand.company_img}
          alt={brand.company_name}
          className="h-12 w-12 object-contain mb-2"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-lg font-bold text-gray-600 mb-2">
          {brand.company_name[0]}
        </div>
      )}
      <span className="text-xs font-medium text-gray-700 leading-tight">
        {brand.company_name}
      </span>
    </div>
  );

  return (
    <div className="flex justify-center min-h-screen">
      <div className="w-full max-w-4xl p-6">
        {/* Translated Title */}
        <CommonTitle title={t("whichCompanyAreYouFrom")} showTitle={true} />

        {/* Search Bar */}
        <div className="max-w-xl mx-auto mb-8 mt-4">
          <div className="flex items-center bg-white rounded-full px-4 py-3 shadow-sm border border-gray-200">
            <span className="text-gray-400 mr-3">🔍</span>
            <input
              type="text"
              placeholder={t("searchCompanyName")}
              className="w-full bg-transparent outline-none text-base text-gray-700 placeholder-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Search Results or Main List */}
        {searchTerm ? (
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-semibold px-2 mb-4 text-gray-800">
              {t("searchResults", "Search Results")}
            </h3>
            {filtered.length > 0 ? (
              <div className="flex overflow-x-auto px-2 py-2 scrollbar-hide gap-4">
                {filtered.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} />
                ))}
              </div>
            ) : (
              <p className="px-2 text-gray-500 text-center py-8">
                {t("noBrandsFound", "No brands found.")}
              </p>
            )}
          </div>
        ) : (
          <div
            className="max-w-4xl mx-auto overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <div className="space-y-8 pb-20">
              {/* Past Companies - Debug info */}
              {console.log("Past Company IDs:", pastCompanyIds)}
              {console.log("Past Companies:", pastCompanies)}

              {pastCompanies.length > 0 && (
                <HorizontalSection
                  title={t("yourPastCompanies", "Your Past Companies")}
                  brands={pastCompanies}
                  BrandCard={BrandCard}
                />
              )}

              {/* Most Popular */}
              {mostPopular.length > 0 && (
                <HorizontalSection
                  title={t("mostPopularBrands", "Most Popular Brands")}
                  brands={mostPopular}
                  BrandCard={BrandCard}
                />
              )}

              {/* Most Searched */}
              {mostSearched.length > 0 && (
                <HorizontalSection
                  title={t("mostSearchedBrands", "Most Searched Brands")}
                  brands={mostSearched}
                  BrandCard={BrandCard}
                />
              )}

              {/* Alphabetical List */}
              {Object.keys(groupedAlphabetically)
                .sort()
                .map((letter) => (
                  <div key={letter} className="mb-6">
                    <h3 className="text-lg font-semibold px-2 mb-3 text-gray-800">
                      {letter}
                    </h3>
                    <div className="flex overflow-x-auto px-2 py-2 scrollbar-hide gap-4">
                      {groupedAlphabetically[letter].map((brand) => (
                        <BrandCard key={brand.id} brand={brand} />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HorizontalSection = ({ title, brands, BrandCard }) => (
  <div className="mb-8">
    <h3 className="text-lg font-semibold px-2 mb-3 text-gray-800">{title}</h3>
    <div className="flex overflow-x-auto px-2 py-2 scrollbar-hide gap-4">
      {brands.map((brand) => (
        <BrandCard key={brand.id} brand={brand} />
      ))}
    </div>
  </div>
);

export default SearchCompany;
